import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { HomePage } from './pages/HomePage';
import { CreditsPage } from './pages/CreditsPage';
import { DownloadsPage } from './pages/DownloadsPage';
import { ContactPage } from './pages/ContactPage';
import { TimecodeNotesPage } from './pages/TimecodeNotesPage';
import { DCPToolPage } from './pages/DCPToolPage';
import { navigation } from './constants';
import { SoundLibraryExplorer } from './pages/SoundLibraryExplorer';
import { CinemmerseProPage } from './pages/CinemmerseProPage';
import { MusicPage } from './pages/MusicPage';
import { NotFoundPage } from './pages/NotFoundPage';

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const utilitiesPaths = ['/timecode-notes', '/dcp-tool', '/sound-library-explorer', '/cinemmerse-pro'];
  const disableAnimation = utilitiesPaths.some((path) => location.pathname.startsWith(path));

  if (disableAnimation) {
    return <div>{children}</div>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{
          duration: 0.6,
          ease: [0.25, 0.46, 0.45, 0.94] as const,
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

const basename = (() => {
  const baseUrl = import.meta.env?.BASE_URL ?? '/';
  const trimmed = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return trimmed || '/';
})();

function App() {
  return (
    <Router basename={basename}>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-zinc-950 text-gray-100">
        <Navbar />
        <main className="flex-1 w-full container mx-auto px-4 pt-20 pb-8 overflow-x-hidden">
          <Routes>
            <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
            <Route path="/credits" element={<PageWrapper><CreditsPage /></PageWrapper>} />
            <Route path="/music" element={<PageWrapper><MusicPage /></PageWrapper>} />
            <Route path="/downloads" element={<PageWrapper><DownloadsPage /></PageWrapper>} />
            <Route path="/contact" element={<PageWrapper><ContactPage /></PageWrapper>} />
            <Route path="/timecode-notes" element={<PageWrapper><TimecodeNotesPage /></PageWrapper>} />
            <Route path="/dcp-tool" element={<PageWrapper><DCPToolPage /></PageWrapper>} />
            <Route path="/sound-library-explorer" element={<PageWrapper><SoundLibraryExplorer /></PageWrapper>} />
            <Route path="/cinemmerse-pro" element={<PageWrapper><CinemmerseProPage /></PageWrapper>} />
            <Route path="*" element={<PageWrapper><NotFoundPage /></PageWrapper>} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
